/**
 * StatusVFXOrchestrator — Declarative VFX driven by status effect state.
 *
 * Complements SkillVFXOrchestrator (which handles skill activation VFX).
 * This system reads `status-effect-vfx.json` and applies/removes tints and
 * VFX primitives on entities as their StatusEffectTracker state changes.
 *
 * Design: frame-polling with diff (ADR-086). Each registered entity is
 * checked every frame — new effects trigger VFX creation, removed effects
 * trigger VFX cleanup. Simpler and equally correct vs. adding event
 * emitters to StatusEffectTracker.
 *
 * @see ADR-086 (status-effect VFX declarations)
 */
import type Phaser from 'phaser';
import type { StatusEffectTracker } from '../systems/StatusEffectTracker';
import type { StatusEffectVfxEntry, StatusEffectVfxMap } from './StatusEffectVfxTypes';
import { VfxFactory } from './VfxFactory';
import { VfxConfigLoader } from './VfxConfigLoader';
import vfxMappings from '../data/status-effect-vfx.json';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Callback returning the entity's current world position and depth. */
type GetPosition = () => { x: number; y: number; depth: number };

/** Callback returning the entity's StatusEffectTracker (may be null). */
type GetTracker = () => StatusEffectTracker | null;

/** Tintable sprite interface — minimal contract for setTint/clearTint. */
interface Tintable {
  setTint(color: number): void;
  clearTint(): void;
}

/** Callback returning the entity's tintable sprite (may be null if dead). */
type GetSprite = () => Tintable | null;

/** Per-entity tracking state. */
interface EntityState {
  getPosition: GetPosition;
  getTracker: GetTracker;
  getSprite: GetSprite;
  /** Status effect IDs that had VFX active last frame. */
  lastActiveEffects: Set<string>;
  /** Per-entity VfxFactory — each entity owns its own primitive instances. */
  factory: VfxFactory;
  /** Effect key → reference count. Tracks how many status effects reference each key. */
  sustainRefCounts: Map<string, number>;
  /** The tint color currently applied, or null if none. */
  currentTint: number | null;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

export class StatusVFXOrchestrator {
  private scene: Phaser.Scene;
  private mappings: Map<string, StatusEffectVfxEntry> = new Map();
  private entities: Map<string, EntityState> = new Map();

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.loadMappings();
  }

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  /**
   * Register an entity for status-effect VFX tracking.
   * @param entityId Unique identifier (e.g. "player", "enemy_0", "enemy_1")
   * @param getPosition Returns current world position + depth
   * @param getTracker Returns the entity's StatusEffectTracker (or null)
   * @param getSprite Returns a tintable sprite reference (or null if dead)
   */
  registerEntity(
    entityId: string,
    getPosition: GetPosition,
    getTracker: GetTracker,
    getSprite: GetSprite,
  ): void {
    // Clean up if re-registering (shouldn't happen, but be safe)
    if (this.entities.has(entityId)) {
      this.unregisterEntity(entityId);
    }

    this.entities.set(entityId, {
      getPosition,
      getTracker,
      getSprite,
      lastActiveEffects: new Set(),
      factory: new VfxFactory(this.scene),
      sustainRefCounts: new Map(),
      currentTint: null,
    });
  }

  /**
   * Unregister an entity — stops and destroys all its active VFX,
   * clears its tint, and releases resources.
   */
  unregisterEntity(entityId: string): void {
    const state = this.entities.get(entityId);
    if (!state) return;

    // Clear tint
    if (state.currentTint !== null) {
      state.getSprite()?.clearTint();
    }

    // Stop all active sustain VFX
    for (const key of state.sustainRefCounts.keys()) {
      state.factory.get(key).stop();
    }
    state.sustainRefCounts.clear();

    // Destroy the entity's factory (kills all primitives)
    state.factory.destroyAll();
    state.lastActiveEffects.clear();

    this.entities.delete(entityId);
  }

  /**
   * Per-frame update. Polls each entity's tracker, diffs against last
   * known state, applies/removes VFX and tints as needed, and repositions
   * active sustain effects.
   */
  update(delta: number): void {
    for (const [entityId, state] of this.entities) {
      const tracker = state.getTracker();
      if (!tracker) {
        // Tracker gone (entity dying?) — remove everything
        if (state.lastActiveEffects.size > 0 || state.currentTint !== null) {
          this.clearAllForEntity(entityId, state);
        }
        continue;
      }

      // Build current set of mapped effect IDs that are active
      const currentEffects = new Set<string>();
      for (const effectId of this.mappings.keys()) {
        if (tracker.has(effectId)) {
          currentEffects.add(effectId);
        }
      }

      // Diff: find added and removed effects
      const added: string[] = [];
      const removed: string[] = [];

      for (const id of currentEffects) {
        if (!state.lastActiveEffects.has(id)) added.push(id);
      }
      for (const id of state.lastActiveEffects) {
        if (!currentEffects.has(id)) removed.push(id);
      }

      // Process removals
      for (const id of removed) {
        this.removeEffectVfx(state, id);
      }

      // Process additions
      for (const id of added) {
        this.applyEffectVfx(state, id);
      }

      // Update last-known state
      state.lastActiveEffects = currentEffects;

      // Resolve tint (recompute every frame — cheap and handles priority changes)
      this.resolveTint(state, currentEffects);

      // Reposition active sustain VFX
      if (state.sustainRefCounts.size > 0) {
        const { x, y, depth } = state.getPosition();
        for (const key of state.sustainRefCounts.keys()) {
          state.factory.get(key).updatePosition(x, y, depth, delta);
        }
      }
    }
  }

  /** Destroy all entities and their VFX — call on scene shutdown. */
  destroy(): void {
    // Collect keys first — unregisterEntity deletes from the map
    const entityIds = [...this.entities.keys()];
    for (const entityId of entityIds) {
      this.unregisterEntity(entityId);
    }
    this.mappings.clear();
  }

  // -----------------------------------------------------------------------
  // Internal
  // -----------------------------------------------------------------------

  /** Load mappings from JSON data, stripping the _comment field. */
  private loadMappings(): void {
    const data = vfxMappings as Record<string, unknown>;
    for (const [key, value] of Object.entries(data)) {
      if (key === '_comment') continue;
      this.mappings.set(key, value as StatusEffectVfxEntry);
    }
  }

  /** Apply VFX primitives for a newly-active status effect on an entity. */
  private applyEffectVfx(state: EntityState, effectId: string): void {
    const entry = this.mappings.get(effectId);
    if (!entry?.effectKeys) return;

    const { x, y, depth } = state.getPosition();
    for (const key of entry.effectKeys) {
      if (!VfxConfigLoader.has(key)) continue;
      const refCount = state.sustainRefCounts.get(key) ?? 0;
      if (refCount === 0) {
        // First reference — start the primitive
        state.factory.get(key).play(x, y, depth);
      }
      state.sustainRefCounts.set(key, refCount + 1);
    }
  }

  /** Remove VFX primitives for a status effect that just expired. */
  private removeEffectVfx(state: EntityState, effectId: string): void {
    const entry = this.mappings.get(effectId);
    if (!entry?.effectKeys) return;

    for (const key of entry.effectKeys) {
      if (!VfxConfigLoader.has(key)) continue;
      const refCount = state.sustainRefCounts.get(key) ?? 0;
      if (refCount <= 1) {
        // Last reference — stop the primitive
        state.factory.get(key).stop();
        state.sustainRefCounts.delete(key);
      } else {
        state.sustainRefCounts.set(key, refCount - 1);
      }
    }
  }

  /**
   * Resolve which tint should be active based on all current effects.
   * Highest tintPriority wins. Only updates the sprite if the tint changed.
   */
  private resolveTint(state: EntityState, currentEffects: Set<string>): void {
    let winningTint: number | null = null;
    let winningPriority = -1;

    for (const id of currentEffects) {
      const entry = this.mappings.get(id);
      if (!entry?.tint) continue;
      const priority = entry.tintPriority ?? 0;
      if (priority > winningPriority) {
        winningPriority = priority;
        winningTint = parseInt(entry.tint, 16);
      }
    }

    // Only touch sprite if the tint actually changed
    if (winningTint !== state.currentTint) {
      const sprite = state.getSprite();
      if (sprite) {
        if (winningTint !== null) {
          sprite.setTint(winningTint);
        } else {
          sprite.clearTint();
        }
      }
      state.currentTint = winningTint;
    }
  }

  /** Clear all VFX and tint for an entity (tracker went null). */
  private clearAllForEntity(_entityId: string, state: EntityState): void {
    // Clear tint
    if (state.currentTint !== null) {
      state.getSprite()?.clearTint();
      state.currentTint = null;
    }

    // Stop all VFX
    for (const key of state.sustainRefCounts.keys()) {
      state.factory.get(key).stop();
    }
    state.sustainRefCounts.clear();
    state.lastActiveEffects.clear();
  }
}
